import React, { useState, useEffect } from "react";
import { Plus, ChevronLeft, CheckCircle, AlertTriangle, Upload, FileText, X } from "lucide-react";
import api from "../../api";
import { K } from "../../theme";
import { Btn, Card, Pill, Field, TextInput } from "../components";
import { fmtDate, todayStr } from "../utils";

const ApplyLeaveScreen = ({ types, balances, freeze, onClose }) => {
  const [type, setType] = useState(types[0]?.cd || "CL");
  const [start, setStart] = useState(""); const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [days, setDays] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [err, setErr] = useState(""); const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false); const [lwpAsk, setLwpAsk] = useState(null);
  const selBal = balances.find(b => b.type === type);
  const selType = types.find(t => t.cd === type);
  const todayMin = todayStr();
  // Whether THIS employee works a 6-day week — previously Saturday was
  // excluded unconditionally for everyone, which made it structurally
  // impossible to apply leave for a Saturday someone actually works.
  // Sunday stays hardcoded off; no per-employee config exists for it.
  const [saturdayWorking, setSaturdayWorking] = useState(false);
  useEffect(() => { api.get('/api/employees/me').then(d => setSaturdayWorking(!!d?.saturday_working)).catch(() => {}); }, []);

  const inFreeze = (ymd) => { if (!freeze?.enabled || !ymd) return false; const md = ymd.slice(5); return freeze.start <= freeze.end ? (md >= freeze.start && md <= freeze.end) : (md >= freeze.start || md <= freeze.end); };

  const genDays = (s, e) => {
    if (!s || !e) return [];
    // A single explicitly-picked day is always selectable (see web Leaves.jsx
    // for why) — only a multi-day range still auto-excludes Sat/Sun.
    if (s === e) return [{ dt: s, tp: "Full Day" }];
    const out = []; let c = new Date(s + "T12:00:00"); const stop = new Date(e + "T12:00:00");
    while (c <= stop) {
      const dow = c.getDay();
      if (dow !== 0 && (dow !== 6 || saturdayWorking)) { const ymd = c.toISOString().slice(0, 10); out.push({ dt: ymd, tp: "Full Day" }); }
      c.setDate(c.getDate() + 1);
    }
    return out;
  };
  const dayList = days.length ? days : genDays(start, end);
  const totalDays = dayList.reduce((s, d) => s + (d.tp === "Full Day" ? 1 : 0.5), 0);
  const avl = selBal?.avl;
  // Only truly-unlimited types (selBal.unl) show "Unlimited" here. A missing
  // balance row (avl null/undefined for a type that isn't unlimited) used to
  // fall through to the same null -> "Unlimited" render as the real
  // unlimited case, while "Available" right above it showed "0" for that
  // exact same missing-data case — a self-contradicting "Available: 0,
  // After booking: Unlimited" display. Falling back to 0 here too keeps
  // both figures consistent.
  const after = (selBal?.unl) ? null : ((avl ?? 0) - totalDays);
  const setDayType = (i, tp) => { const nd = (days.length ? days : genDays(start, end)).map((d, idx) => idx === i ? { ...d, tp } : d); setDays(nd); };

  const validate = () => {
    if (!start || !end) return "Select dates";
    if (start < todayMin) return "Backdated leaves not allowed";
    if (end < start) return "End date can't be before start";
    if (dayList.some(d => inFreeze(d.dt))) return `Some dates fall in freeze window (${freeze.start}–${freeze.end})`;
    if (!reason.trim()) return "Reason required";
    if (selType?.docReq && !docFile) return "Supporting document is required for " + selType.nm;
    if (totalDays <= 0) return "Select at least 0.5 day";
    return null;
  };

  const submit = async (autoLwp) => {
    setSaving(true); setErr("");
    try {
      let r;
      if (docFile) {
        const fd = new FormData();
        fd.append("leave_type", type);
        fd.append("start_date", start);
        fd.append("end_date", end);
        fd.append("days_detail", JSON.stringify(dayList));
        fd.append("reason", reason);
        fd.append("auto_lwp", autoLwp ? "true" : "false");
        fd.append("document", docFile);
        r = await api.upload('/api/leaves', fd);
      } else {
        r = await api.post('/api/leaves', { leave_type: type, start_date: start, end_date: end, days_detail: dayList, reason, auto_lwp: !!autoLwp });
      }
      if (!r) return;
      onClose(r.converted_to_lwp
        ? `Submitted as LWP (auto-converted from ${r.original_type} due to insufficient balance).`
        : "Leave request submitted successfully.");
    } catch (e) { setErr(e.message || "Failed"); setSaving(false); setConfirm(false); setLwpAsk(null); }
  };

  const onSubmit = () => {
    const v = validate(); if (v) return setErr(v);
    if (after !== null && after < 0 && !selBal?.unl && type !== "LWP") {
      setLwpAsk({ avail: avl || 0, requested: totalDays, deficit: Math.abs(after) }); return;
    }
    setErr(""); setConfirm(true);
  };

  return <div className="px-4 pt-4 pb-[100px]">
    <div className="flex items-center gap-[10px] mb-[14px]">
      <button onClick={onClose} className="bg-transparent border-0 p-2 text-t1 cursor-pointer"><ChevronLeft size={22} /></button>
      <div className="text-[20px] font-extrabold text-t0">Apply Leave</div>
    </div>

    {freeze?.enabled && <div className="px-3 py-[10px] mb-3 bg-ab border border-ad rounded-[10px] text-[14px] text-a flex items-center gap-[6px]"><AlertTriangle size={14} />Leave freeze active: {freeze.start} to {freeze.end}</div>}

    <Card>
      <Field label="Leave Type">
        <select value={type} onChange={e => { setType(e.target.value); setDays([]); setDocFile(null); setErr(""); }} className="w-full px-[14px] py-3 bg-bg2 border border-b0 rounded-[10px] text-[16px] text-t0 outline-none">
          {types.map(t => <option key={t.cd} value={t.cd}>{t.nm}{t.docReq ? " · 📎 doc required" : ""}</option>)}
        </select>
        {selType?.docReq && <div className="mt-2 inline-flex items-center gap-1 px-[8px] py-[3px] rounded-full text-[14px] font-semibold" style={{ background: K.a + "20", color: K.a, border: `1px solid ${K.a}40` }}>📎 Document Required</div>}
      </Field>

      <div className="flex gap-2 mb-3">
        <div className="flex-1"><Field label="Start"><TextInput type="date" min={todayMin} value={start} onChange={e => { const v = e.target.value; setStart(v); if (end && end < v) setEnd(v); setDays([]); }} /></Field></div>
        <div className="flex-1"><Field label="End"><TextInput type="date" min={start || todayMin} value={end} onChange={e => { setEnd(e.target.value); setDays([]); }} /></Field></div>
      </div>

      {dayList.length > 0 && <div className="rounded-[10px] border border-b0 mb-3 overflow-hidden">
        <div className="px-[10px] py-[6px] bg-bg2 text-[14px] text-t3 uppercase">Per-day duration</div>
        {dayList.map((d, i) => { const fz = inFreeze(d.dt); return <div key={d.dt} className={`flex items-center justify-between px-[10px] py-2 ${i > 0 ? "border-t border-b0" : ""} ${fz ? "bg-rb" : "bg-transparent"}`}>
          <div className={`text-[14px] flex-1 ${fz ? "text-r" : "text-t1"}`}>{fmtDate(d.dt)}{fz && <span className="ml-[6px] text-[12px] text-r font-bold">FROZEN</span>}</div>
          <select value={d.tp} onChange={e => setDayType(i, e.target.value)} className="px-2 py-1 bg-bg2 border border-b0 rounded-md text-[14px] text-t0">
            <option value="Full Day">Full Day</option><option value="1st Half">1st Half</option><option value="2nd Half">2nd Half</option>
          </select>
        </div>; })}
        <div className="flex justify-between px-[10px] py-2 bg-bg2 text-[15px] font-bold border-t border-b0">
          <span className="text-t2">Total</span><span className="text-t0">{totalDays} day(s)</span>
        </div>
      </div>}

      <Field label="Reason"><textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Brief reason for leave…" className="w-full min-h-[80px] px-[14px] py-3 bg-bg2 border border-b0 rounded-[10px] text-[15px] text-t0 outline-none box-border resize-y" /></Field>

      {selType?.docReq && <Field label="Supporting Document *">
        {docFile ? (
          <div className="flex items-center gap-2 px-3 py-[10px] rounded-[10px] border-2 border-g bg-gb">
            <FileText size={18} color={K.g} />
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-g truncate">{docFile.name}</div>
              <div className="text-[14px] text-t3">{(docFile.size / 1024 / 1024).toFixed(2)} MB</div>
            </div>
            <button type="button" onClick={() => setDocFile(null)} className="bg-transparent border-0 p-1 text-r cursor-pointer"><X size={18} /></button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center gap-1 p-4 rounded-[10px] border-2 border-dashed border-b1 bg-bg2 cursor-pointer active:bg-bg1">
            <Upload size={22} color={K.t2} />
            <div className="text-[15px] font-semibold text-t1">Tap to upload</div>
            <div className="text-[14px] text-t3">PDF, JPG, PNG, DOC · Max 10 MB</div>
            <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={e => {
              const f = e.target.files[0]; if (!f) return;
              if (f.size > 10 * 1024 * 1024) { setErr(`File too large: ${(f.size / 1024 / 1024).toFixed(2)} MB. Max 10 MB.`); e.target.value = ""; return; }
              if (!/\.(pdf|doc|docx|png|jpe?g)$/i.test(f.name)) { setErr("Unsupported file type. Use PDF, JPG, PNG, or DOC."); e.target.value = ""; return; }
              setErr(""); setDocFile(f);
            }} />
          </label>
        )}
      </Field>}

      <div className="px-3 py-[10px] rounded-[10px] bg-bg2 mb-3 text-[14px]">
        <div className="flex justify-between"><span className="text-t3">Available</span><span className="text-g font-bold">{selBal?.unl ? "Unlimited" : (avl ?? 0)}</span></div>
        <div className="flex justify-between mt-[3px]"><span className="text-t3">Requested</span><span className="text-t0 font-bold">{totalDays}</span></div>
        <div className="flex justify-between mt-[3px] pt-[6px] border-t border-b0"><span className="text-t1 font-semibold">After booking</span><span className="font-bold" style={{ color: after !== null && after < 0 ? K.r : K.g }}>{after === null ? "Unlimited" : after}</span></div>
      </div>

      {err && <div className="px-3 py-2 rounded-lg bg-rb text-r text-[14px] mb-[10px] flex items-center gap-[6px]"><AlertTriangle size={13} />{err}</div>}

      <Btn full onClick={onSubmit} disabled={saving || (selType?.docReq && !docFile)}>{selType?.docReq && !docFile ? "Upload document to continue" : "Continue"}</Btn>
    </Card>

    {confirm && <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[200] flex items-end justify-center" onMouseDown={() => !saving && setConfirm(false)}>
      <div className="animate-modalIn bg-s rounded-t-2xl p-5 w-full max-w-[712px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-[10px]"><CheckCircle size={20} color={K.p} /><span className="text-base font-extrabold text-t0">Confirm Leave</span></div>
        <div className="text-[14px] text-t2 mb-3">Your manager and centre HR will be notified.</div>
        <div className="p-3 bg-bg2 rounded-[10px] text-[15px] mb-[14px]">
          <div className="flex justify-between mb-[5px]"><span className="text-t3">Type</span><span className="text-t0 font-bold">{selType?.nm || type}</span></div>
          <div className="flex justify-between mb-[5px]"><span className="text-t3">From → To</span><span className="text-t0 font-bold">{fmtDate(start)} → {fmtDate(end)}</span></div>
          <div className="flex justify-between mb-[5px]"><span className="text-t3">Days</span><span className="text-t0 font-bold">{totalDays}</span></div>
          {docFile && <div className="flex justify-between items-center pt-[6px] mt-[6px] border-t border-b0"><span className="text-t3">Attachment</span><span className="text-t0 font-semibold truncate ml-2 flex items-center gap-1"><FileText size={13} color={K.g} />{docFile.name}</span></div>}
        </div>
        <div className="flex gap-2">
          <Btn full variant="secondary" onClick={() => setConfirm(false)} disabled={saving}>Back</Btn>
          <Btn full onClick={() => submit(false)} disabled={saving}>{saving ? "Submitting…" : "Submit"}</Btn>
        </div>
      </div>
    </div>}

    {lwpAsk && <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[200] flex items-end justify-center" onMouseDown={() => !saving && setLwpAsk(null)}>
      <div className="animate-modalIn bg-s rounded-t-2xl p-5 w-full max-w-[712px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-[10px]"><AlertTriangle size={20} color={K.a} /><span className="text-base font-extrabold text-t0">Insufficient Balance</span></div>
        <div className="text-[15px] text-t2 mb-[10px]">Available: <b>{lwpAsk.avail}</b>, requested: <b>{lwpAsk.requested}</b>, short: <b className="text-r">{lwpAsk.deficit}</b>.</div>
        <div className="text-[14px] text-t2 px-3 py-[10px] bg-rb rounded-lg mb-[14px] border border-rd">The full {totalDays}-day request will be submitted as <strong>Leave Without Pay (LWP)</strong>. Salary deduction applies.</div>
        <div className="flex gap-2">
          <Btn full variant="secondary" onClick={() => setLwpAsk(null)} disabled={saving}>Cancel</Btn>
          <Btn full variant="danger" onClick={() => submit(true)} disabled={saving}>{saving ? "Submitting…" : "Submit as LWP"}</Btn>
        </div>
      </div>
    </div>}
  </div>;
};

export const Leaves = ({ balances, types, freeze, onRefresh }) => {
  const [view, setView] = useState("list");
  const [requests, setRequests] = useState([]);
  const [msg, setMsg] = useState("");
  const activeTypes = types.filter(t => t.active !== false);

  useEffect(() => { api.get('/api/leaves?view=my').then(d => setRequests(d.data || d || [])).catch(() => {}); }, []);

  if (view === "apply") return <ApplyLeaveScreen types={activeTypes} balances={balances} freeze={freeze} onClose={(m) => {
    setView("list"); onRefresh && onRefresh(); api.get('/api/leaves?view=my').then(d => setRequests(d.data || d || []));
    if (m) { setMsg(m); setTimeout(() => setMsg(""), 4000); }
  }} />;

  return <div className="px-4 pt-4 pb-[100px]">
    <div className="flex items-center justify-between mb-[14px]">
      <div className="text-[22px] font-extrabold text-t0">Leaves</div>
      <Btn icon={Plus} onClick={() => setView("apply")} style={{ padding: "10px 14px", fontSize: 13 }}>Apply</Btn>
    </div>

    {msg && <div className="px-[10px] py-2 mb-3 bg-gb text-g rounded-lg text-[14px] flex items-center gap-[6px]"><CheckCircle size={13} />{msg}</div>}

    <div className="grid grid-cols-2 gap-[10px] mb-[18px]">
      {balances.map(b => <Card key={b.type} style={{ padding: "12px 14px", borderLeft: `4px solid ${b.clr || K.p}` }}>
        <div className="text-[14px] font-semibold text-t1 mb-[6px] leading-[1.2]">{b.nm}</div>
        <div className="flex gap-[14px]">
          <div><div className="text-[12px] text-t3">AVAILABLE</div><div className="text-[18px] font-extrabold text-g">{b.unl ? "∞" : b.avl ?? 0}</div></div>
          <div><div className="text-[12px] text-t3">BOOKED</div><div className="text-[18px] font-extrabold text-t0">{b.used ?? 0}</div></div>
        </div>
      </Card>)}
    </div>

    <div className="text-[14px] font-bold text-t2 uppercase tracking-[0.05em] mb-[10px]">My Requests</div>
    {requests.length === 0 && <Card><div className="text-center text-t3 text-[15px] py-2">No leave requests yet</div></Card>}
    {requests.map(r => {
      const stClr = r.status === "Done" ? K.g : r.status === "Rejected" ? K.r : r.status === "Cancelled" ? K.t3 : K.a;
      return <Card key={r.id} className="mb-2" style={{ padding: "12px 14px" }}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-[6px] mb-1">
              <Pill c={r.typeClr || K.p}>{r.typeNm || r.type}</Pill>
              <span className="text-[15px] font-bold text-t0">{r.days}d</span>
            </div>
            <div className="text-[14px] text-t1">{fmtDate(r.start)} → {fmtDate(r.end)}</div>
            {r.reason && <div className="text-[14px] text-t3 mt-1 leading-[1.4]">{r.reason}</div>}
          </div>
          <Pill c={stClr}>{r.status}</Pill>
        </div>
      </Card>;
    })}
  </div>;
};
